﻿import React, { useState } from "react";
import { Box } from "@mui/material";
import {
  ShepSearchBar,
  ShepTab,
  ShepTabs,
  ShepHorizontalMenu,
} from "@/components/GenericComponents/ShepComponents";
import { createAlert } from "@/features/homepage/alertSliceList";
import { useGridApiContext } from "@mui/x-data-grid-pro";
import { useDispatch } from "react-redux";
import {
  importTabTypesEnum,
  setFileImportPopupOpen,
} from "@/features/maintenance/fileImportSlice";
import axios from "@/features/axiosHelper";
import AddThreePLQualifiersPopup from "./AddWorkflowLookup/AddThreePLQualifiersPopup";
import AddDefaultWarehousePopup from "./AddWorkflowLookup/AddDefaultWarehousePopup";
import AddImpliedDCPopup from "./AddWorkflowLookup/AddImpliedDCPopup";
import { useWorkflowLookups } from "./WorkflowLookupsContext";
import AddCustomerWarehousePopup from "./AddWorkflowLookup/AddCustomerWarehousePopup";
import { WorkflowLookupTypes } from "@/components/Maintenance/MaintenanceTypes";

const WorkflowLookupsToolbar = () => {
  const { selectedTab, setSelectedTab, fetchData } = useWorkflowLookups();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const apiRef = useGridApiContext();
  const dispatch = useDispatch();

  const handleWorkflowLookupsTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    if (selectedTab !== newValue) {
      const filterModel = {
        items: [
          {
            field: "WorkflowLookupType",
            operator: "equals",
            value: newValue.toString(),
          },
        ],
      };
      apiRef.current.setFilterModel(filterModel);
      updateFilterModel("Reference", searchText, "contains");
    }
  };

  const handleSearch = (event) => {
    setSearchText(event.target.value);
    if (apiRef.current) {
      apiRef.current.setQuickFilterValues([event.target.value]);
    }
  };

  const handleDelete = () => {
    if (!apiRef.current) return;
    const selectedRows = apiRef.current.getSelectedRows();
    const rowIds = Array.from(selectedRows.values()).map(
      (rowData) => rowData["WorkflowLookupId"]
    );
    if (rowIds.length) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          title: "This Workflow Lookup will be deleted",
          message: "Are you sure you want to delete this record?",
          buttonLabel: "Yes",
          buttonLabel2: "No",
          onButtonClick: () => deleteWorkflowLookups({ rowIds }),
        })
      );
    } else {
      dispatch(
        createAlert({
          status: "Error",
          alertType: "single",
          message: "Please select the records you want to delete first.",
        })
      );
    }
  };

  const deleteWorkflowLookups = async ({ rowIds = [] }) => {
    if (!rowIds.length || !apiRef.current) return;
    try {
      await Promise.all(
        rowIds.map(async (id) => {
          await axios.delete(`/workflowlookups/${id}`);
        })
      );
      // Remove deleted rows from the grid
      apiRef.current.updateRows(
        rowIds.map((WorkflowLookupId) => ({
          WorkflowLookupId,
          _action: "delete",
        }))
      );
      dispatch(
        createAlert({
          status: "Success",
          alertType: "result",
          title: "Deletion Successful!",
          message: "Workflow Lookup records deleted correctly.",
          buttonLabel: "Close",
        })
      );
      fetchData();
    } catch (error) {
      dispatch(
        createAlert({
          status: "Error",
          alertType: "result",
          title: "Deletion failed.",
          message:
            "Error deleting workflow lookup(s). Please check if the record is referenced elsewhere.",
          buttonLabel: "Close",
        })
      );
    }
  };

  const updateFilterModel = (property, value, operation = "equals") => {
    if (!apiRef.current) return;
    const items = [...apiRef.current.state.filter.filterModel.items];
    const dataTypeModel = items.filter((x) => x.field === property);
    if (dataTypeModel && dataTypeModel.length) {
      dataTypeModel[0]["value"] = value && value.toString();
    } else {
      items.push({
        field: property,
        operator: operation,
        value: value && value.toString(),
      });
    }
    const newModel = {
      ...apiRef.current.state.filter.filterModel,
      items: items,
    };
    apiRef.current.setFilterModel(newModel);
  };

  const handleExport = async () => {
    if (!apiRef.current) return;
    const selectedRows = apiRef.current.getSelectedRows();
    const selectedRowIds = Array.from(selectedRows.values()).map(
      (row) => row.WorkflowLookupId
    );

    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");

    const tabEntry = Object.values(WorkflowLookupTypes).find(
      (tab) => tab.value === selectedTab
    );
    const formattedDataTypeLabel = tabEntry
      ? tabEntry.label.replace(/\s+/g, "_")
      : selectedTab;

    const filename = `workflow_lookups_${formattedDataTypeLabel}_${timestamp}.csv`;

    try {
      const response = await axios.post(
        "/workflowlookups/export-workflow-lookups-data",
        {
          workflowLookupType: selectedTab,
          selectedRowIds: selectedRowIds.join(","),
          searchText: searchText.trim(),
        },
        {
          responseType: "blob",
          headers: { "Content-Type": "application/json" },
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      dispatch(
        createAlert({
          status: "Error",
          message:
            "Error exporting data: " + (error?.response?.data || error.message),
        })
      );
      console.error("Error exporting data:", error);
    }
  };

  const actions = [
    {
      label: "Import",
      onClick: () =>
        dispatch(
          setFileImportPopupOpen({
            open: true,
            tabType: importTabTypesEnum.workflowLookup,
            dataType: selectedTab,
          })
        ),
    },
    { label: "Export", onClick: handleExport },
    { label: "Add", onClick: () => setAddDialogOpen(true) },
    { label: "Delete", onClick: handleDelete, variant: "danger" },
  ];

  return (
    <div sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <ShepTabs
        variant="gridSelector"
        value={selectedTab}
        onChange={(event, newValue) =>
          handleWorkflowLookupsTabChange(event, newValue)
        }
        sx={{ display: "flex", flexDirection: "row", width: "100%" }}
      >
        <ShepTab
          variant="gridSelector"
          key={WorkflowLookupTypes.ThreePLQualifiers.value}
          label={WorkflowLookupTypes.ThreePLQualifiers.label}
          id={WorkflowLookupTypes.ThreePLQualifiers.value}
          value={WorkflowLookupTypes.ThreePLQualifiers.value}
          aria-controls="tabpanel-0"
        />
        <ShepTab
          variant="gridSelector"
          key={WorkflowLookupTypes.DefaultWarehouse.value}
          label={WorkflowLookupTypes.DefaultWarehouse.label}
          id={WorkflowLookupTypes.DefaultWarehouse.value}
          value={WorkflowLookupTypes.DefaultWarehouse.value}
          aria-controls="tabpanel-1"
        />
        <ShepTab
          variant="gridSelector"
          key={WorkflowLookupTypes.ImpliedDC.value}
          label={WorkflowLookupTypes.ImpliedDC.label}
          id={WorkflowLookupTypes.ImpliedDC.value}
          value={WorkflowLookupTypes.ImpliedDC.value}
          aria-controls="tabpanel-2"
        />
        <ShepTab
          variant="gridSelector"
          key={WorkflowLookupTypes.InventoryWarehouse.value}
          label={WorkflowLookupTypes.InventoryWarehouse.label}
          id={WorkflowLookupTypes.InventoryWarehouse.value}
          value={WorkflowLookupTypes.InventoryWarehouse.value}
          aria-controls="tabpanel-2"
        />
      </ShepTabs>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "25px 0",
          backgroundColor: "var(--disabled)",
        }}
      >
        <ShepSearchBar
          placeholder="Search Workflow Lookups"
          onChange={handleSearch}
          sx={{ width: 600 }}
        />
        <Box sx={{ marginLeft: "auto", display: "flex" }}>
          <ShepHorizontalMenu actions={actions} />
        </Box>
      </div>

      {selectedTab === WorkflowLookupTypes.ThreePLQualifiers.value && (
        <AddThreePLQualifiersPopup
          open={addDialogOpen}
          onClose={() => setAddDialogOpen(false)}
          workflowLookupType={selectedTab}
          fetchData={fetchData}
        />
      )}
      {selectedTab === WorkflowLookupTypes.DefaultWarehouse.value && (
        <AddDefaultWarehousePopup
          open={addDialogOpen}
          onClose={() => setAddDialogOpen(false)}
          workflowLookupType={selectedTab}
          fetchData={fetchData}
        />
      )}
      {selectedTab === WorkflowLookupTypes.ImpliedDC.value && (
        <AddImpliedDCPopup
          open={addDialogOpen}
          onClose={() => setAddDialogOpen(false)}
          workflowLookupType={selectedTab}
          fetchData={fetchData}
        />
      )}
      {selectedTab === WorkflowLookupTypes.InventoryWarehouse.value && (
          <AddCustomerWarehousePopup
            open={addDialogOpen}
            onClose={() => setAddDialogOpen(false)}
            workflowLookupType={selectedTab}
            fetchData={fetchData}
          />
        )
      }
    </div>
  );
};

export default WorkflowLookupsToolbar;
